/**
 * 1Password item share links (« liens sécurisés 1Password »): detection, canonical form, field kinds, fill codes and
 * the placeholders that replace a link everywhere. Pure and environment-neutral (Worker, web client, E2E).
 * https://share.1password.com/s#<secret>: the fragment alone decrypts the item, so the whole URL is a secret.
 *
 * Two rules: the LENIENT one decides what gets masked (any share.1password.com/.ca/.eu address carrying a fragment,
 * scheme optional, JSON-escaped slashes, `%23` for `#`, plus the legacy `onepassword://share/…`: masking a false
 * positive costs nothing); the STRICT one (`canonical`) decides what can be kept and used.
 */

export interface ShareLinkMatch {
  start: number;
  end: number;
  /** Canonical URL (`https://share.1password.com/s…#<fragment>`), null when the link cannot be used. */
  url: string | null;
  problem: "incomplete" | "unsupported" | null;
}

/** Longest text scanned: the regex is linear, longer texts are scanned up to here and masked as a whole beyond. */
const MAX_SCAN = 1_000_000;
const ZERO_WIDTH = /[​-‍⁠﻿]/g;
const FAST_PATH = /1password/i;

const LENIENT_SOURCE =
  String.raw`(?:https?:\\?\/\\?\/)?(?:www\.)?share\.1password\.(?:com|ca|eu)(?:\\?\/[^\s#<>"'«»()\[\]{}|\\^` +
  "`" +
  String.raw`]*)?(?:#|%23)[A-Za-z0-9_\-+\/=%]*|onepassword:\/\/share\/[^\s<>"'«»]+`;

/** A fresh lenient matcher (global regexes keep state: one per scan). */
export function lenientShareLinkRe(): RegExp {
  return new RegExp(LENIENT_SOURCE, "gi");
}

/** Mail clients wrap long lines: a short fragment right before a line break continues on the next line. */
const WRAPPED_TAIL = /^\r?\n([A-Za-z0-9_\-+\/=]{1,64})/;
const STRICT_FRAGMENT = /^[A-Za-z0-9_\-+/]{40,64}={0,2}$/;

/** Removes zero-width characters (a link split by one would escape the scan), as memory/secrets.ts does. */
export function stripInvisible(text: string): string {
  return ZERO_WIDTH.test(text) ? text.replace(ZERO_WIDTH, "") : text;
}

/** The usable form of a raw match, or why it cannot be used. */
export function canonicalShareLink(raw: string): { url: string | null; problem: ShareLinkMatch["problem"] } {
  if (/^onepassword:/i.test(raw)) return { url: null, problem: "unsupported" };
  let s = raw.replace(/\r?\n/g, "").replace(/\\\//g, "/").replace(/%23/i, "#");
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    return { url: null, problem: "unsupported" };
  }
  if (url.hostname !== "share.1password.com" || (url.pathname !== "/s" && url.pathname !== "/s/") || url.username || url.password) {
    return { url: null, problem: "unsupported" };
  }
  const fragment = url.hash.replace(/^#/, "");
  if (!STRICT_FRAGMENT.test(fragment)) return { url: null, problem: "incomplete" };
  return { url: `https://share.1password.com/s${url.search}#${fragment}`, problem: null };
}

/**
 * Every share link of `text` (pass a text already through `stripInvisible`: offsets refer to the text given). Fast
 * path: no "1password" in the text → [].
 */
export function findShareLinks(text: string): ShareLinkMatch[] {
  if (!FAST_PATH.test(text)) return [];
  const scanned = text.length > MAX_SCAN ? text.slice(0, MAX_SCAN) : text;
  const out: ShareLinkMatch[] = [];
  const re = lenientShareLinkRe();
  for (let m = re.exec(scanned); m; m = re.exec(scanned)) {
    if (m[0].length === 0) {
      re.lastIndex++;
      continue;
    }
    let end = m.index + m[0].length;
    const hash = Math.max(m[0].lastIndexOf("#"), m[0].toLowerCase().lastIndexOf("%23"));
    const fragmentLength = hash >= 0 ? m[0].length - hash - (m[0][hash] === "#" ? 1 : 3) : 0;
    if (!/^onepassword:/i.test(m[0]) && fragmentLength >= 1 && fragmentLength <= 42) {
      const tail = WRAPPED_TAIL.exec(scanned.slice(end, end + 70));
      if (tail) end += tail[0].length;
    }
    const raw = scanned.slice(m.index, end);
    out.push({ start: m.index, end, ...canonicalShareLink(raw) });
    re.lastIndex = end;
  }
  return out;
}

/** True when the text holds a share link (lenient rule). */
export function containsShareLink(text: string): boolean {
  if (!FAST_PATH.test(text)) return false;
  return lenientShareLinkRe().test(stripInvisible(text));
}

/**
 * Replaces every share link of `text` with `render(match, index)`. Returns the text unchanged when it holds none
 * (otherwise without its zero-width characters). Beyond MAX_SCAN characters a text holding a link is cut there.
 */
export function replaceShareLinks(text: string, render: (m: ShareLinkMatch, i: number) => string): string {
  if (!FAST_PATH.test(text)) return text;
  const clean = stripInvisible(text);
  const matches = findShareLinks(clean);
  if (!matches.length) return text;
  let out = "";
  let at = 0;
  matches.forEach((m, i) => {
    out += clean.slice(at, m.start) + render(m, i);
    at = m.end;
  });
  const rest = clean.slice(at);
  // A text longer than the scan limit may hide another link past it: the unscanned part is dropped.
  return out + (clean.length > MAX_SCAN ? `${rest.slice(0, MAX_SCAN - at)}…` : rest);
}

/** The web app's mask (the app is French). */
export const APP_MASK = "🔐 lien 1Password";
/** The mask of texts only the system reads (R2 copies, logs). */
export const SYSTEM_MASK = "🔐 lien 1Password masqué";
/** Model requests (safety net): never shown to a user; the E2E asserts it never appears. */
export const SAFETY_NET_MASK = "🔐 lien 1Password masqué (filet de sécurité)";

export function maskShareLinks(text: string, mask: string = APP_MASK): string {
  return replaceShareLinks(text, () => mask);
}

/**
 * Masks every string of a JSON-like value in place (objects and arrays, any depth, iterative walk) and returns the
 * value with the number of links masked. `skip`: objects left untouched (with everything under them).
 */
export function maskShareLinksDeep<T>(root: T, mask: string = SYSTEM_MASK, skip?: (node: Record<string, unknown>) => boolean): { value: T; count: number } {
  let count = 0;
  const maskString = (s: string): string => {
    if (!FAST_PATH.test(s)) return s;
    const clean = stripInvisible(s);
    const n = findShareLinks(clean).length;
    if (!n) return s;
    count += n;
    return replaceShareLinks(clean, () => mask);
  };
  if (typeof root === "string") return { value: maskString(root) as T, count };
  if (root === null || typeof root !== "object") return { value: root, count };
  const stack: unknown[] = [root];
  const seen = new Set<unknown>();
  while (stack.length) {
    const node = stack.pop();
    if (node === null || typeof node !== "object" || seen.has(node)) continue;
    seen.add(node);
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        const v = node[i];
        if (typeof v === "string") node[i] = maskString(v);
        else if (v !== null && typeof v === "object") stack.push(v);
      }
      continue;
    }
    const rec = node as Record<string, unknown>;
    if (skip?.(rec)) continue;
    for (const k of Object.keys(rec)) {
      const v = rec[k];
      if (typeof v === "string") rec[k] = maskString(v);
      else if (v !== null && typeof v === "object") stack.push(v);
    }
  }
  return { value: root, count };
}

/**
 * Every placeholder Pulsez writes in place of a secret (share link or pasted value), French and English: they all
 * start with « 🔐 » and end with the vault number, a reason in brackets, or « masqué(e) » / « hidden » (kept in
 * step with src/worker/agent/vault/copy.ts and with the masks above).
 */
export const PLACEHOLDER_RE =
  /🔐 [^\n()🔐]{1,60}?(?:\((?:coffre n°|vault #)\d{1,7}\)|\([^()\n]{1,60}\)|masqu[ée]e?|hidden)/gu;

/** True when the text holds a placeholder (a fresh regex: PLACEHOLDER_RE is global). */
export function containsPlaceholder(text: string): boolean {
  return new RegExp(PLACEHOLDER_RE.source, "u").test(text);
}

/**
 * The comparison key of a chat line and the message it echoes: links AND placeholders become « § » (the web app
 * reconciles its pending bubble, raw link inside, with the stored line, placeholder inside).
 */
export function echoKey(text: string): string {
  return maskShareLinks(text, "§").replace(new RegExp(PLACEHOLDER_RE.source, "gu"), "§").replace(/\s+/g, " ").trim();
}

// ───────────── labels ─────────────

const EDGE_PUNCTUATION = /^[\s:;,.!?\-–—→>()[\]«»"“”']+|[\s:;,.!?\-–—→>()[\]«»"“”']+$/gu;
const FILLER = new Set(
  "voici voilà voila tiens ci-joint ci-dessous le la les l' un une mon ma mes du de des d' lien link 1password pour for c'est it's here here's is my the a an stp svp please merci thanks et and"
    .split(" ")
    .map((w) => w.toLowerCase()),
);
const LABEL_MAX = 60;
const SIDE_MAX = 80;

/** Edge punctuation, then edge filler words, repeatedly. */
function trimLabel(raw: string): string {
  let s = raw.replace(/\s+/g, " ").replace(EDGE_PUNCTUATION, "");
  for (let guard = 0; guard < 20; guard++) {
    const before = s;
    const words = s.split(" ").filter(Boolean);
    while (words.length && isFiller(words[0]!)) words.shift();
    while (words.length && isFiller(words[words.length - 1]!)) words.pop();
    s = words.join(" ").replace(EDGE_PUNCTUATION, "");
    // « l'accès » / « d'Amazon »: the elided article is a filler too.
    s = s.replace(/^(?:l|d)['’](?=\p{L})/iu, "");
    if (s === before) break;
  }
  return s.trim();
}

function isFiller(word: string): boolean {
  const w = word.toLowerCase().replace(/[’]/g, "'").replace(EDGE_PUNCTUATION, "");
  return w === "" || FILLER.has(w);
}

function cutAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max + 1);
  const space = cut.lastIndexOf(" ");
  return (space > max / 2 ? cut.slice(0, space) : text.slice(0, max)).trim();
}

/**
 * The link's label from the user's own words around it (same line, up to the previous/next link or placeholder):
 * the words before it when they still hold a word with letters, else those after it, else the first 6 words of the
 * text without links. At most 60 characters, cut at a word boundary. null when nothing is left. `text` must be the
 * one the match was found in.
 */
export function labelAround(text: string, m: Pick<ShareLinkMatch, "start" | "end">): string | null {
  const lineStart = text.lastIndexOf("\n", m.start - 1) + 1;
  const nextBreak = text.indexOf("\n", m.end);
  const lineEnd = nextBreak < 0 ? text.length : nextBreak;
  let before = text.slice(lineStart, m.start);
  let after = text.slice(m.end, lineEnd);
  // Up to the previous / next link or placeholder only.
  const cutBefore = lastBoundary(before);
  if (cutBefore >= 0) before = before.slice(cutBefore);
  const cutAfter = firstBoundary(after);
  if (cutAfter >= 0) after = after.slice(0, cutAfter);
  before = before.slice(-SIDE_MAX);
  after = after.slice(0, SIDE_MAX);
  const hasWord = (s: string) => /\p{L}{2,}/u.test(s);
  const b = trimLabel(before);
  if (hasWord(b)) return cutAtWord(b, LABEL_MAX);
  const a = trimLabel(after);
  if (hasWord(a)) return cutAtWord(a, LABEL_MAX);
  const whole = trimLabel(
    maskShareLinks(text, " ")
      .replace(new RegExp(PLACEHOLDER_RE.source, "gu"), " ")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 6)
      .join(" "),
  );
  return hasWord(whole) ? cutAtWord(whole, LABEL_MAX) : null;
}

function boundaries(s: string): { start: number; end: number }[] {
  const out: { start: number; end: number }[] = [];
  for (const m of findShareLinks(s)) out.push({ start: m.start, end: m.end });
  for (const m of s.matchAll(new RegExp(PLACEHOLDER_RE.source, "gu"))) out.push({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length });
  return out;
}

function lastBoundary(s: string): number {
  return boundaries(s).reduce((max, b) => Math.max(max, b.end), -1);
}

function firstBoundary(s: string): number {
  return boundaries(s).reduce((min, b) => (min < 0 || b.start < min ? b.start : min), -1);
}
